// MACHINERY V2 (Stroje) — the fleet PAGE HEAD below 1280 (M2.16a).
//
// Rendered truth: mobile/screenshots/01-filter-b-chosen.png (phone) and
// 07-v1-tablet-768.png (tablet — same head, wider). Reference impl:
// mobile/machinery-mobile.jsx MbPageHead (:30-41).
//
// The desktop toolbar has no page title because the rail and the nav carry the
// context; below 1280 the rail is gone, so the head is where "which page am I
// on" and "how big is this fleet" live.
//
// ============================================================
//  THE KEBAB IS A MANAGER RULING, AND IT REVISES AN EARLIER ONE
// ============================================================
// "Spravovat skupiny" sits in a kebab BESIDE "+ Pridat", not in the global nav
// drawer. A page action stays on the page it acts on — the drawer is pure
// platform nav, mounted by MachineryV2Layout over the WHOLE v2 subtree (fleet,
// katalog, profile, SR, add), so a fleet-only action placed there would follow
// the user onto four pages it does not apply to and would have to be unpicked
// again at M2.12 when that nav is promoted app-wide.
//
// BOTH ACTIONS ARE Write-GATED, TOGETHER (§10, and the same pairing
// FleetToolbar.tsx:245-267 makes on desktop): a Worker sees neither, so the
// whole cluster collapses to nothing rather than to an empty kebab.
//
// THE META LINE COUNTS THE BASE SET, not the filtered one — `data.flat`, which
// is what the sheet's own STAV counts are computed over
// (useFleetPageData.ts:154, `countStates(flat, states)`). One number for "how
// big is my fleet" that does not move when a filter is toggled; what the filter
// narrowed to is the sheet's primary count and the fleet itself.
//
// The alarm segment is omitted at zero, exactly as the wall's alarm counter is
// (wall/wallHeaderCounters.tsx:10-12): a standing "0 v alarme" is a numeral
// people stop reading, and its absence is the signal. `stale-alarm` is summed
// into it for the same reason the wall sums it — a last-known alarm is still an
// alarm.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MenuContent } from "../chrome/chromeMenuContent";
import { MbKebabGlyph } from "../mobile/mobileGlyphs";
import { FleetPageData } from "./fleetPageData";

export interface FleetMobileHeaderProps {
  data: FleetPageData;
  canWrite: boolean;
  onManageGroups: () => void;
  onAddMachine: () => void;
}

const FleetMobileHeader: React.FC<FleetMobileHeaderProps> = ({
  data,
  canWrite,
  onManageGroups,
  onAddMachine,
}) => {
  const { translate } = useTranslation("myMachinery");

  const running = data.stateCounts.running || 0;
  const alarm = (data.stateCounts.alarm || 0) + (data.stateCounts["stale-alarm"] || 0);
  const meta = [
    translate("myMachinery:v2.fleet.machineCount", { count: data.flat.length }),
    `${running} ${translate("myMachinery:v2.fleet.wall.cRunning")}`,
    alarm > 0 ? `${alarm} ${translate("myMachinery:v2.fleet.wall.cAlarm")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px 10px",
        flexShrink: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--ink)",
          }}
        >
          {translate("myMachinery:v2.mobile.fleetTitle")}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            marginTop: 1,
          }}
        >
          {meta}
        </div>
      </div>

      <span style={{ flex: 1 }} />

      {canWrite && (
        <>
          <button
            type="button"
            className="btn btn-mod"
            onClick={onAddMachine}
            style={{
              height: 44,
              // >= 44px BOTH axes; "+ Pridat" clears it, the floor is for the
              // translation that does not.
              minWidth: 44,
              borderRadius: 10,
              whiteSpace: "nowrap",
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>+</span>
            {translate("myMachinery:v2.mobile.add")}
          </button>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="btn"
                // NAMES THE TRIGGER, NOT THE MENU'S ONE ITEM. With
                // "Spravovat skupiny" here, a screen reader announced the
                // action, the user opened the menu, and the only item in it
                // announced the same words again — the trigger claiming to BE
                // the action it merely reveals. The item keeps `manageGroups`.
                aria-label={translate("myMachinery:v2.mobile.moreActions")}
                style={{
                  width: 44,
                  height: 44,
                  padding: 0,
                  borderRadius: 10,
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MbKebabGlyph />
              </button>
            </DropdownMenu.Trigger>
            {/* The canonical portal wrapper — scope class, transparent Content,
                live palette, page-menu tier z-index 1290. */}
            <MenuContent width={214}>
              <DropdownMenu.Item className="mi" onSelect={onManageGroups}>
                <span>{translate("myMachinery:manageGroups")}</span>
              </DropdownMenu.Item>
            </MenuContent>
          </DropdownMenu.Root>
        </>
      )}
    </div>
  );
};

export default FleetMobileHeader;
